import { useState, useMemo, useRef } from "react";
import "./FilesView.css";
import ContextMenu, { MenuItem } from "./ContextMenu";
import {
  Folder,
  NoteListItem,
  createFolder as apiCreateFolder,
  deleteFolder as apiDeleteFolder,
  renameFolder as apiRenameFolder,
  reorderFolders as apiReorderFolders,
  togglePinFolder as apiTogglePinFolder,
  updateFolderColor as apiUpdateFolderColor,
  updateNote as apiUpdateNote,
} from "./api";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  useDroppable,
  useDraggable,
  defaultDropAnimationSideEffects,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Icon components
const ChevronRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="icon chevron">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const ChevronDown = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="icon chevron">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const FolderIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="icon folder">
    <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
  </svg>
);

const FolderOpenIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="icon folder">
    <path d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h6a2 2 0 012 2v1" />
    <path d="M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
  </svg>
);

const FileIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="icon file">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="icon trash">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);

const PencilIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="icon">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="icon">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const MoreIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="icon">
    <circle cx="12" cy="5" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="12" cy="19" r="2" />
  </svg>
);

const PinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="icon">
    <path d="M12 17v5" />
    <path d="M9 11V6a3 3 0 0 1 6 0v5" />
    <path d="M5 11h14l-1.5 6H6.5L5 11z" />
  </svg>
);

const PaletteIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="icon">
    <circle cx="13.5" cy="6.5" r="2" />
    <circle cx="17.5" cy="10.5" r="2" />
    <circle cx="8.5" cy="7.5" r="2" />
    <circle cx="6.5" cy="12.5" r="2" />
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.555C21.965 6.012 17.461 2 12 2z" />
  </svg>
);

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="icon">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

// Preset folder colors
const FOLDER_COLORS = [
  { name: "None", value: null },
  // Reds
  { name: "Red", value: "#ef4444" },
  { name: "Rose", value: "#f43f5e" },
  { name: "Crimson", value: "#dc2626" },
  // Oranges
  { name: "Orange", value: "#f97316" },
  { name: "Amber", value: "#f59e0b" },
  // Yellows
  { name: "Yellow", value: "#eab308" },
  { name: "Lime", value: "#84cc16" },
  // Greens
  { name: "Green", value: "#22c55e" },
  { name: "Emerald", value: "#10b981" },
  { name: "Teal", value: "#14b8a6" },
  // Blues
  { name: "Cyan", value: "#06b6d4" },
  { name: "Sky", value: "#0ea5e9" },
  { name: "Blue", value: "#3b82f6" },
  { name: "Indigo", value: "#6366f1" },
  // Purples
  { name: "Violet", value: "#8b5cf6" },
  { name: "Purple", value: "#a855f7" },
  { name: "Fuchsia", value: "#d946ef" },
  // Pinks
  { name: "Pink", value: "#ec4899" },
  // Neutrals
  { name: "Slate", value: "#64748b" },
  { name: "Gray", value: "#9ca3af" },
];

interface ContextMenuState {
  x: number;
  y: number;
  type: "folder" | "note";
  id: number | null;
}

interface FilesViewProps {
  notes: NoteListItem[];
  folders: Folder[];
  currentNoteId: number | null;
  currentFolderId: number | null;
  folderFocused: boolean;
  openTabs: number[];
  expandedFolders: Set<number | null>;
  onNoteSelect: (noteId: number) => void;
  onFolderSelect: (folderId: number | null) => void;
  onRefreshNotes: () => void;
  onRefreshFolders: () => void;
  onNewNote: (folderId: number | null) => void;
  onDeleteNote: (noteId: number) => void;
  onCloseNotes: (noteIds: number[]) => void;
  onRenameNote: (noteId: number, newName: string) => void;
  onExpandedFoldersChange: (folders: Set<number | null>) => void;
  onExportPdf: (noteId: number) => void;
}

// Draggable Note component
function DraggableNote({
  note,
  isSelected,
  onSelect,
  onContextMenu,
  onMenuClick,
}: {
  note: NoteListItem;
  isSelected: boolean;
  onSelect: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onMenuClick: (e: React.MouseEvent) => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `note-${note.id}`,
    data: { type: "note", note },
  });

  return (
    <div
      ref={setNodeRef}
      className={`note-item ${isSelected ? "selected" : ""} ${isDragging ? "dragging" : ""}`}
      onClick={onSelect}
      onContextMenu={onContextMenu}
      {...listeners}
      {...attributes}
    >
      <FileIcon />
      <span className="note-name">{note.name}</span>
      <button className="menu-btn" onClick={(e) => { e.stopPropagation(); onMenuClick(e); }} title="Menu">
        <MoreIcon />
      </button>
    </div>
  );
}

// General folder component (header is droppable for notes)
function GeneralFolder({
  isExpanded,
  isSelected,
  isDropTarget,
  onToggle,
  onContextMenu,
  onMenuClick,
  children,
}: {
  isExpanded: boolean;
  isSelected: boolean;
  isDropTarget: boolean;
  onToggle: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onMenuClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}) {
  const { setNodeRef } = useDroppable({
    id: "general-folder-header",
    data: { type: "folder", folderId: null },
  });

  return (
    <div className="folder-item">
      <div
        ref={setNodeRef}
        className={`folder-header ${isSelected ? "selected" : ""} ${isDropTarget ? "drag-over" : ""}`}
        onClick={onToggle}
        onContextMenu={onContextMenu}
      >
        <span className="folder-chevron">
          {isExpanded ? <ChevronDown /> : <ChevronRight />}
        </span>
        <span className="folder-icon">
          {isExpanded ? <FolderOpenIcon /> : <FolderIcon />}
        </span>
        <span className="folder-name">General</span>
        <button className="menu-btn" onClick={onMenuClick} title="Menu">
          <MoreIcon />
        </button>
      </div>
      {isExpanded && children}
    </div>
  );
}

// Droppable folder content area
function DroppableFolderContent({
  folderId,
  children,
  isOver,
  hasActive,
}: {
  folderId: number | null;
  children: React.ReactNode;
  isOver: boolean;
  hasActive: boolean;
}) {
  const { setNodeRef } = useDroppable({
    id: folderId === null ? "folder-null" : `folder-${folderId}`,
    data: { type: "folder", folderId },
  });

  const classes = ["note-list"];
  if (isOver) classes.push("drop-target");
  if (hasActive) classes.push("has-active");

  return (
    <div ref={setNodeRef} className={classes.join(" ")}>
      {children}
    </div>
  );
}

// Sortable folder component
function SortableFolder({
  folder,
  isExpanded,
  isSelected,
  isPinned,
  isDropTarget,
  onToggle,
  onContextMenu,
  onMenuClick,
  children,
}: {
  folder: Folder;
  isExpanded: boolean;
  isSelected: boolean;
  isPinned: boolean;
  isDropTarget: boolean;
  onToggle: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onMenuClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `sortable-folder-${folder.id}`,
    data: { type: "sortable-folder", folder },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
    zIndex: isDragging ? 1 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`folder-item ${isDragging ? "dragging" : ""}`}
    >
      <div
        className={`folder-header ${isSelected ? "selected" : ""} ${isDropTarget ? "drag-over" : ""}`}
        onClick={onToggle}
        onContextMenu={onContextMenu}
        {...listeners}
        {...attributes}
      >
        <span className="folder-chevron">
          {isExpanded ? <ChevronDown /> : <ChevronRight />}
        </span>
        <span className="folder-icon">
          {isExpanded ? <FolderOpenIcon /> : <FolderIcon />}
        </span>
        <span className="folder-name">{folder.name}</span>
        {folder.color && (
          <span
            className="folder-color-dot"
            style={{ backgroundColor: folder.color }}
          />
        )}
        {isPinned && (
          <span className="pin-indicator">
            <PinIcon />
          </span>
        )}
        <button className="menu-btn" onClick={onMenuClick} title="Menu">
          <MoreIcon />
        </button>
      </div>
      {isExpanded && children}
    </div>
  );
}

export default function FilesView({
  notes,
  folders,
  currentNoteId,
  currentFolderId,
  folderFocused,
  expandedFolders,
  onNoteSelect,
  onFolderSelect,
  onRefreshNotes,
  onRefreshFolders,
  onNewNote,
  onDeleteNote,
  onCloseNotes,
  onRenameNote,
  onExpandedFoldersChange,
  onExportPdf,
}: FilesViewProps) {
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [colorPickerFolderId, setColorPickerFolderId] = useState<number | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [dropTargetFolderId, setDropTargetFolderId] = useState<number | null | "none">("none");
  const expandTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Drop animation configuration
  const dropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: "0.5",
        },
      },
    }),
  };

  // Configure sensors for mouse, touch, and keyboard
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleCreateFolder = async () => {
    const name = prompt("Enter folder name:");
    if (!name) return;

    try {
      await apiCreateFolder(name);
      onRefreshFolders();
    } catch (err) {
      console.error("Failed to create folder:", err);
    }
  };

  const toggleFolder = (folderId: number | null) => {
    const next = new Set(expandedFolders);
    if (next.has(folderId)) {
      next.delete(folderId);
    } else {
      next.add(folderId);
    }
    onExpandedFoldersChange(next);
    onFolderSelect(folderId);
  };

  // Context menu handlers
  const handleFolderContextMenu = (e: React.MouseEvent, folderId: number | null) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, type: "folder", id: folderId });
  };

  const handleNoteContextMenu = (e: React.MouseEvent, noteId: number) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, type: "note", id: noteId });
  };

  const handleMenuButtonClick = (
    e: React.MouseEvent,
    type: "folder" | "note",
    id: number | null
  ) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setContextMenu({ x: rect.right, y: rect.top, type, id });
  };

  const handleRenameFolder = async (folderId: number) => {
    const folder = folders.find((f) => f.id === folderId);
    if (!folder) return;

    const newName = prompt("Enter new folder name:", folder.name);
    if (!newName || newName === folder.name) return;

    try {
      await apiRenameFolder(folderId, newName);
      onRefreshFolders();
    } catch (err) {
      console.error("Failed to rename folder:", err);
    }
  };

  const handleRenameNote = async (noteId: number) => {
    const note = notes.find((n) => n.id === noteId);
    if (!note) return;

    const newName = prompt("Enter new note name:", note.name);
    if (!newName || newName === note.name) return;

    try {
      await apiUpdateNote(noteId, { name: newName });
      onRenameNote(noteId, newName);
      onRefreshNotes();
    } catch (err) {
      console.error("Failed to rename note:", err);
    }
  };

  const handleTogglePin = async (folderId: number) => {
    try {
      await apiTogglePinFolder(folderId);
      onRefreshFolders();
    } catch (err) {
      console.error("Failed to toggle pin:", err);
    }
  };

  const handleSetFolderColor = async (folderId: number, color: string | null) => {
    try {
      await apiUpdateFolderColor(folderId, color);
      onRefreshFolders();
    } catch (err) {
      console.error("Failed to update folder color:", err);
    }
    setColorPickerFolderId(null);
  };

  const getFolderMenuItems = (folderId: number | null): MenuItem[] => {
    const items: MenuItem[] = [
      {
        label: "New Note",
        icon: <PlusIcon />,
        onClick: () => onNewNote(folderId),
      },
    ];

    if (folderId !== null) {
      const folder = folders.find(f => f.id === folderId);
      const isPinned = folder?.is_pinned === 1;

      items.push(
        {
          label: isPinned ? "Unpin" : "Pin",
          icon: <PinIcon />,
          onClick: () => handleTogglePin(folderId),
        },
        {
          label: "Color",
          icon: <PaletteIcon />,
          onClick: () => {
            setContextMenu(null);
            setColorPickerFolderId(folderId);
          },
        },
        {
          label: "Rename",
          icon: <PencilIcon />,
          onClick: () => handleRenameFolder(folderId),
        },
        {
          label: "Delete",
          icon: <TrashIcon />,
          onClick: async () => {
            const notesInFolder = notes.filter(n => n.folder_id === folderId);
            const noteCount = notesInFolder.length;
            const message = noteCount > 0
              ? `Delete this folder and ${noteCount} note${noteCount === 1 ? '' : 's'}? This cannot be undone.`
              : "Delete this folder?";
            if (!confirm(message)) return;
            try {
              const { deletedNoteIds } = await apiDeleteFolder(folderId);
              if (deletedNoteIds && deletedNoteIds.length > 0) {
                onCloseNotes(deletedNoteIds);
              }
              onRefreshFolders();
              onRefreshNotes();
              if (currentFolderId === folderId) {
                onFolderSelect(null);
              }
            } catch (err) {
              console.error("Failed to delete folder:", err);
            }
          },
          danger: true,
        }
      );
    }

    return items;
  };

  const getNoteMenuItems = (noteId: number): MenuItem[] => [
    {
      label: "Export PDF",
      icon: <DownloadIcon />,
      onClick: () => {
        onExportPdf(noteId);
        setContextMenu(null);
      },
    },
    {
      label: "Rename",
      icon: <PencilIcon />,
      onClick: () => handleRenameNote(noteId),
    },
    {
      label: "Delete",
      icon: <TrashIcon />,
      onClick: () => {
        if (!confirm("Delete this note?")) return;
        onDeleteNote(noteId);
      },
      danger: true,
    },
  ];

  // DnD handlers
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragOver = (event: any) => {
    const { active, over } = event;
    if (!over) {
      setDropTargetFolderId("none");
      if (expandTimeoutRef.current) {
        clearTimeout(expandTimeoutRef.current);
        expandTimeoutRef.current = null;
      }
      return;
    }

    // Only show drop target when dragging notes
    const activeData = active.data.current;
    if (activeData?.type !== "note") {
      setDropTargetFolderId("none");
      return;
    }

    let targetFolderId: number | null | "none" = "none";
    const overData = over.data.current;

    if (overData?.type === "folder") {
      targetFolderId = overData.folderId;
    } else if (overData?.type === "sortable-folder") {
      targetFolderId = (overData.folder as Folder).id;
    }

    // Auto-expand folder after hovering for 500ms
    if (targetFolderId !== "none" && targetFolderId !== dropTargetFolderId) {
      if (expandTimeoutRef.current) {
        clearTimeout(expandTimeoutRef.current);
      }

      if (!expandedFolders.has(targetFolderId)) {
        expandTimeoutRef.current = setTimeout(() => {
          const next = new Set(expandedFolders);
          next.add(targetFolderId as number | null);
          onExpandedFoldersChange(next);
        }, 500);
      }
    }

    setDropTargetFolderId(targetFolderId);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setDropTargetFolderId("none");

    // Clear any pending expand timeout
    if (expandTimeoutRef.current) {
      clearTimeout(expandTimeoutRef.current);
      expandTimeoutRef.current = null;
    }

    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    // Handle note drop onto folder (either droppable area or sortable folder header)
    if (activeData?.type === "note") {
      let targetFolderId: number | null | undefined;

      if (overData?.type === "folder") {
        targetFolderId = overData.folderId as number | null;
      } else if (overData?.type === "sortable-folder") {
        targetFolderId = (overData.folder as Folder).id;
      }

      if (targetFolderId !== undefined) {
        const note = activeData.note as NoteListItem;

        if (note.folder_id !== targetFolderId) {
          // Sync with API and refresh notes
          apiUpdateNote(note.id, { folder_id: targetFolderId })
            .then(() => onRefreshNotes())
            .catch(err => {
              console.error("Failed to move note:", err);
              onRefreshNotes(); // Refresh to show actual state
            });
        }
        return;
      }
    }

    // Handle folder reordering
    if (activeData?.type === "sortable-folder" && overData?.type === "sortable-folder") {
      const activeFolder = activeData.folder as Folder;
      const overFolder = overData.folder as Folder;

      if (activeFolder.id === overFolder.id) return;

      // Find indices in the full folders array
      const oldIndex = folders.findIndex(f => f.id === activeFolder.id);
      const newIndex = folders.findIndex(f => f.id === overFolder.id);

      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        const reorderedFolders = arrayMove(folders, oldIndex, newIndex);
        const newOrder = reorderedFolders.map(f => f.id);
        apiReorderFolders(newOrder)
          .then(() => onRefreshFolders())
          .catch(err => {
            console.error("Failed to reorder folders:", err);
          });
      }
    }
  };

  // Split folders into pinned and unpinned
  const pinnedFolders = useMemo(() => folders.filter(f => f.is_pinned === 1), [folders]);
  const unpinnedFolders = useMemo(() => folders.filter(f => f.is_pinned === 0), [folders]);

  // Get sortable IDs
  const pinnedFolderIds = useMemo(
    () => pinnedFolders.map(f => `sortable-folder-${f.id}`),
    [pinnedFolders]
  );
  const unpinnedFolderIds = useMemo(
    () => unpinnedFolders.map(f => `sortable-folder-${f.id}`),
    [unpinnedFolders]
  );

  // Group notes by folder
  const notesByFolder = useMemo(() => {
    const grouped = new Map<number | null, NoteListItem[]>();

    // Initialize with all folders
    folders.forEach((f) => grouped.set(f.id, []));
    grouped.set(null, []); // No folder section

    // Distribute notes
    notes.forEach((note) => {
      const folderId = note.folder_id;
      if (!grouped.has(folderId)) {
        grouped.set(folderId, []);
      }
      grouped.get(folderId)!.push(note);
    });

    return grouped;
  }, [notes, folders]);

  // Get active dragged item for overlay
  const activeNote = useMemo(() => {
    if (!activeId?.startsWith("note-")) return null;
    const noteId = parseInt(activeId.replace("note-", ""));
    return notes.find(n => n.id === noteId) || null;
  }, [activeId, notes]);

  const activeFolder = useMemo(() => {
    if (!activeId?.startsWith("sortable-folder-")) return null;
    const folderId = parseInt(activeId.replace("sortable-folder-", ""));
    return folders.find(f => f.id === folderId) || null;
  }, [activeId, folders]);

  // Render notes for a folder
  const renderNotes = (folderId: number | null) => {
    const folderNotes = notesByFolder.get(folderId) || [];
    const hasActiveNote = currentNoteId !== null && folderNotes.some(n => n.id === currentNoteId);
    return (
      <DroppableFolderContent
        folderId={folderId}
        isOver={dropTargetFolderId === folderId}
        hasActive={hasActiveNote}
      >
        {folderNotes.map((note) => (
          <DraggableNote
            key={note.id}
            note={note}
            isSelected={currentNoteId === note.id}
            onSelect={() => onNoteSelect(note.id)}
            onContextMenu={(e) => handleNoteContextMenu(e, note.id)}
            onMenuClick={(e) => handleMenuButtonClick(e, "note", note.id)}
          />
        ))}
        {folderNotes.length === 0 && (
          <div className="empty-folder">No notes</div>
        )}
      </DroppableFolderContent>
    );
  };

  return (
    <div className="files-view">
      <button className="create-note-btn" onClick={() => onNewNote(null)}>
        + New Note
      </button>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="folder-list">
          {/* General (no folder) section - always at top */}
          <GeneralFolder
            isExpanded={expandedFolders.has(null)}
            isSelected={folderFocused && currentFolderId === null}
            isDropTarget={dropTargetFolderId === null}
            onToggle={() => toggleFolder(null)}
            onContextMenu={(e) => handleFolderContextMenu(e, null)}
            onMenuClick={(e) => handleMenuButtonClick(e, "folder", null)}
          >
            {renderNotes(null)}
          </GeneralFolder>

          {/* Pinned folders section */}
          {pinnedFolders.length > 0 && (
            <>
              <div className="section-divider">
                <span className="section-label">Pinned</span>
              </div>
              <SortableContext items={pinnedFolderIds} strategy={verticalListSortingStrategy}>
                {pinnedFolders.map((folder) => (
                  <SortableFolder
                    key={folder.id}
                    folder={folder}
                    isExpanded={expandedFolders.has(folder.id)}
                    isSelected={folderFocused && currentFolderId === folder.id}
                    isPinned={true}
                    isDropTarget={dropTargetFolderId === folder.id}
                    onToggle={() => toggleFolder(folder.id)}
                    onContextMenu={(e) => handleFolderContextMenu(e, folder.id)}
                    onMenuClick={(e) => handleMenuButtonClick(e, "folder", folder.id)}
                  >
                    {renderNotes(folder.id)}
                  </SortableFolder>
                ))}
              </SortableContext>
            </>
          )}

          {/* Regular folders section */}
          {unpinnedFolders.length > 0 && (
            <>
              <div className="section-divider">
                <span className="section-label">Folders</span>
              </div>
              <SortableContext items={unpinnedFolderIds} strategy={verticalListSortingStrategy}>
                {unpinnedFolders.map((folder) => (
                  <SortableFolder
                    key={folder.id}
                    folder={folder}
                    isExpanded={expandedFolders.has(folder.id)}
                    isSelected={folderFocused && currentFolderId === folder.id}
                    isPinned={false}
                    isDropTarget={dropTargetFolderId === folder.id}
                    onToggle={() => toggleFolder(folder.id)}
                    onContextMenu={(e) => handleFolderContextMenu(e, folder.id)}
                    onMenuClick={(e) => handleMenuButtonClick(e, "folder", folder.id)}
                  >
                    {renderNotes(folder.id)}
                  </SortableFolder>
                ))}
              </SortableContext>
            </>
          )}
        </div>

        {/* Drag overlay for visual feedback */}
        <DragOverlay dropAnimation={dropAnimation}>
          {activeNote && (
            <div className="note-item dragging-overlay">
              <FileIcon />
              <span className="note-name">{activeNote.name}</span>
            </div>
          )}
          {activeFolder && (
            <div className="folder-header dragging-overlay">
              <span className="folder-icon">
                <FolderIcon />
              </span>
              <span className="folder-name">{activeFolder.name}</span>
            </div>
          )}
        </DragOverlay>
      </DndContext>

      <button className="create-folder-btn" onClick={handleCreateFolder}>
        + New Folder
      </button>

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={
            contextMenu.type === "folder"
              ? getFolderMenuItems(contextMenu.id)
              : getNoteMenuItems(contextMenu.id!)
          }
          onClose={() => setContextMenu(null)}
        />
      )}

      {colorPickerFolderId !== null && (
        <div className="color-picker-overlay" onClick={() => setColorPickerFolderId(null)}>
          <div className="color-picker" onClick={(e) => e.stopPropagation()}>
            <div className="color-picker-title">Select Color</div>
            <div className="color-picker-grid">
              {FOLDER_COLORS.map((color) => (
                <button
                  key={color.name}
                  className={`color-option ${color.value === null ? "no-color" : ""}`}
                  style={color.value ? { backgroundColor: color.value } : undefined}
                  onClick={() => handleSetFolderColor(colorPickerFolderId, color.value)}
                  title={color.name}
                >
                  {color.value === null && "×"}
                </button>
              ))}
            </div>
            <div className="color-picker-custom">
              <label className="color-picker-label">Custom hex:</label>
              <div className="color-picker-hex-row">
                <input
                  type="color"
                  className="color-picker-native"
                  onChange={(e) => handleSetFolderColor(colorPickerFolderId, e.target.value)}
                />
                <input
                  type="text"
                  className="color-picker-hex-input"
                  placeholder="#ffffff"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const input = e.currentTarget.value.trim();
                      if (/^#?[0-9A-Fa-f]{6}$/.test(input)) {
                        const hex = input.startsWith("#") ? input : `#${input}`;
                        handleSetFolderColor(colorPickerFolderId, hex);
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
